import { useState, useEffect, useMemo, useRef } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ArrowLeft, Copy, Percent, Building2, FileText, Plus, Edit2, Trash2, Loader2, ChevronRight, MapPin, Search } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LOAN_TYPE_LABELS, LoanType } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { canManagePayouts } from "@/lib/rbac";
import { useAppConfig } from "@/hooks/useAppConfig";
import {
  getAllLocations,
  getLoanTypesForLocation,
  getLendersForLocationLoanType,
  type LocationCard as LocationCardData,
} from "@/lib/bankLoanTypeLocations";
import { getLenderLogo } from "@/lib/LenderLogos";
import { matchOption, matchLoanTypeOption } from "@/lib/askbotDeeplinks";
import { getBankLogoUrl, type BankLogoMap } from "@/lib/bankLogos";
import {
  getAllPayoutRates,
  savePayoutRate,
  deletePayoutRate,
  type PayoutRate,
  seedPayoutRates,
} from "@/services/payoutService";
import { payoutsData } from "@/data/payoutsData";

// Reverse lookup: display label (e.g. "Home Loan") -> internal LoanType enum key.
const LABEL_TO_LOAN_TYPE: Record<string, LoanType> = {
  ...Object.fromEntries(
    Object.entries(LOAN_TYPE_LABELS).map(([key, label]) => [label.toLowerCase(), key as LoanType])
  ),
  // Common label variants used in config / lender-logo maps
  "loan against property (lap)": "lap",
  "loan against property": "lap",
};

// Same icon set used by Banks & Products (ManualMatcher) for loan-type cards.
const LOAN_TYPE_ICONS: Record<string, string> = {
  "Home Loan": "🏠",
  "Loan Against Property (LAP)": "🏢",
  "Personal Loan": "💳",
  "Business Loan": "💼",
  "Car Loan": "🚗",
  "Education Loan": "🎓",
  "Gold Loan": "🥇",
  "Credit Card": "💴",
  "Overdraft": "🏦",
  "Working Capital": "⚙️",
};

interface SimplifiedPayout {
  id: string;
  location: string;
  lender: string;
  loanType: LoanType;
  maxPayout: string;
  dsaCode: string;
  remarks: string;
}

const loanTypes: LoanType[] = ['home_loan', 'lap', 'personal_loan', 'business_loan', 'car_loan'];

// ── Shared step UI (mirrors ManualMatcher / Banks & Products) ────────────────
function StepLabel({ n, label }: { n: number; label: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-white text-xs font-bold shrink-0">{n}</span>
      <p className="text-sm font-semibold text-foreground">{label}</p>
    </div>
  );
}

function SearchInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative mb-3">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all"
      />
    </div>
  );
}

function PickCard({ emoji, label, selected, onClick }: { emoji: string; label: string; selected: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1.5 p-2.5 sm:p-3 rounded-2xl border-2 transition-all duration-200 w-full min-h-[72px] sm:min-h-[80px]
        ${selected
          ? "border-green-500 bg-green-50 dark:bg-green-950/30 shadow-md scale-[1.02]"
          : "border-border bg-card hover:border-green-300 hover:bg-green-50/40 dark:hover:bg-green-950/10 hover:scale-[1.01]"
        }`}>
      <span className="text-lg sm:text-xl">{emoji}</span>
      <span className={`text-[10px] sm:text-xs font-semibold text-center leading-tight ${selected ? "text-green-700 dark:text-green-400" : "text-foreground"}`}>
        {label}
      </span>
    </button>
  );
}

function LenderCard({ lender, loanType, selected, onClick, bankLogoUrls }: { lender: string; loanType: string; selected: boolean; onClick: () => void; bankLogoUrls: BankLogoMap }) {
  const logo = getBankLogoUrl(bankLogoUrls, lender) ?? getLenderLogo(lender, loanType);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [lender, loanType]);

  return (
    <button onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1.5 p-2.5 sm:p-3 rounded-2xl border-2 transition-all duration-200 w-full min-h-[80px] sm:min-h-[90px]
        ${selected
          ? "border-green-500 bg-green-50 dark:bg-green-950/30 shadow-md ring-2 ring-green-200 dark:ring-green-800"
          : "border-border bg-card hover:border-green-300 hover:bg-green-50/40 dark:hover:bg-green-950/10"
        }`}>
      {logo && !failed ? (
        <img src={logo} alt={lender} className="h-8 w-auto max-w-[72px] object-contain" onError={() => setFailed(true)} />
      ) : (
        <div className="h-9 w-9 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center text-green-700 dark:text-green-300 text-sm font-bold shrink-0">
          {lender.charAt(0).toUpperCase()}
        </div>
      )}
      <span className={`text-[9px] sm:text-[10px] font-semibold text-center leading-tight line-clamp-2 px-0.5
        ${selected ? "text-green-700 dark:text-green-400" : "text-muted-foreground"}`}>
        {lender}
      </span>
    </button>
  );
}

// ── Breadcrumb chip row (same style as Banks & Products) ──────────────────────
// Shows: 📍 Location > Loan Type > Lender (all clickable, mirrors ManualMatcher)
function Breadcrumb({
  location,
  loanType,
  lender,
  onLocationClick,
  onLoanTypeClick,
  onLenderClick,
}: {
  location: string;
  loanType?: string;
  lender?: string;
  onLocationClick: () => void;
  onLoanTypeClick?: () => void;
  onLenderClick?: () => void;
}) {
  return (
    <div className="flex items-center gap-1 flex-wrap text-xs sm:text-sm text-muted-foreground mb-3 bg-muted/40 rounded-xl px-3 py-2">
      <MapPin className="h-3.5 w-3.5 text-green-500 shrink-0" />
      <button
        onClick={onLocationClick}
        className="font-semibold text-green-600 hover:underline truncate max-w-[120px] sm:max-w-none"
      >
        {location}
      </button>
      {loanType && (
        <>
          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          <button
            onClick={onLoanTypeClick}
            className="font-semibold text-green-600 hover:underline truncate max-w-[120px] sm:max-w-none"
          >
            {loanType}
          </button>
        </>
      )}
      {lender && (
        <>
          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          <button
            onClick={onLenderClick}
            className="font-semibold text-green-600 hover:underline truncate max-w-[140px] sm:max-w-none"
          >
            {lender}
          </button>
        </>
      )}
    </div>
  );
}

export default function PayoutsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Latches once an AskBot deep link has seeded the cascade below.
  const seededFromLinkRef = useRef(false);
  const { user } = useAuth();
  const canManage = canManagePayouts(user);
  const { config, loading: configLoading } = useAppConfig();
  const [payouts, setPayouts] = useState<SimplifiedPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [selectedLoanTypeLabel, setSelectedLoanTypeLabel] = useState<string>("");
  const [selectedLender, setSelectedLender] = useState<string>("");
  const [locationSearch, setLocationSearch] = useState("");
  const [loanSearch, setLoanSearch] = useState("");
  const [lenderSearch, setLenderSearch] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editPayout, setEditPayout] = useState<SimplifiedPayout | null>(null);
  const [formData, setFormData] = useState({
    location: "",
    lender: "",
    loanType: "home_loan" as LoanType,
    maxPayout: "",
    dsaCode: "",
    remarks: "",
  });

  useEffect(() => { loadPayouts(); }, [user?.id]);

  const loadPayouts = async () => {
    try {
      setLoading(true);
      const rates = await getAllPayoutRates();
      if (rates.length === 0) {
        if (user?.id) {
          toast.info("Migrating payout data to Firestore...");
          await seedPayoutRates(payoutsData, user.id);
          const seededRates = await getAllPayoutRates();
          setPayouts(convertToSimplified(seededRates));
          toast.success("Payout data migrated successfully");
        }
      } else {
        setPayouts(convertToSimplified(rates));
      }
    } catch (error) {
      console.error('Failed to load payouts:', error);
      toast.error("Failed to load payout data");
    } finally {
      setLoading(false);
    }
  };

  const convertToSimplified = (rates: PayoutRate[]): SimplifiedPayout[] =>
    rates.map(rate => ({
      id: rate.id,
      location: rate.location || "",
      lender: rate.lender,
      loanType: rate.loanType,
      maxPayout: rate.maxPayout,
      dsaCode: rate.dsaCode,
      remarks: rate.remarks || formatSlabsAsRemarks(rate.slabs),
    }));

  const formatSlabsAsRemarks = (slabs: any[]): string => {
    if (!slabs || slabs.length === 0) return '';
    return `Slabs: ${slabs.map(s => `${s.range}: ${s.payout}`).join(', ')}`;
  };

  // ── derived data: three-layer cascade Location → Loan Type → Lender ──────────
  // appConfig/main.bankLoanTypeLocations stores the data in REVERSE order
  // ({ [lender]: { [loanType]: locations[] } }); these helpers invert it.
  //
  // config.locationCards is the admin-curated full city list (key/label/emoji)
  // — it includes cities that may not yet be mapped to any lender. Only show
  // locations that actually appear in bankLoanTypeLocations, borrowing card
  // metadata (emoji) from the curated list where available.
  const locationCards: LocationCardData[] = useMemo(() => {
    const mapped = getAllLocations(config.bankLoanTypeLocations);
    const byLabel = new Map(
      config.locationCards.map((c) => [c.label.toLowerCase(), c]),
    );
    return mapped.map(
      (loc) =>
        byLabel.get(loc.toLowerCase()) ?? { key: loc, label: loc, emoji: "📍" },
    );
  }, [config.locationCards, config.bankLoanTypeLocations]);

  const filteredLocationCards = useMemo(() =>
    locationCards.filter(l => l.label.toLowerCase().includes(locationSearch.toLowerCase())),
    [locationCards, locationSearch]);

  // ── AskBot deep link ────────────────────────────────────────────────────────
  // Links from the chatbot arrive as ?location=&loanType=&lender=. Seed the
  // cascade one step at a time, stopping at the first value that matches
  // nothing — a stale link then degrades into the normal picker at that step
  // instead of a blank screen. Payout grids with no location (national ones)
  // send no `location` and correctly land on step 1.
  //
  // Runs once, and clears the params afterwards, so that walking back up the
  // breadcrumb isn't immediately undone by a re-seed.
  useEffect(() => {
    if (seededFromLinkRef.current || configLoading || locationCards.length === 0) return;

    const wantLocation = searchParams.get("location");
    const wantLoanType = searchParams.get("loanType");
    const wantLender   = searchParams.get("lender");
    if (!wantLocation && !wantLoanType && !wantLender) return;

    seededFromLinkRef.current = true;

    // Match on either spelling, but store the card's `key` — that is what the
    // card click handler sets and what the cascade helpers expect.
    const norm = (s: string) => s.trim().toLowerCase();
    const locCard = wantLocation
      ? locationCards.find(
          (c) => norm(c.key) === norm(wantLocation) || norm(c.label) === norm(wantLocation),
        )
      : undefined;
    const location = locCard?.key;

    const loanTypeLabel = location
      ? matchLoanTypeOption(
          getLoanTypesForLocation(config.bankLoanTypeLocations, location),
          wantLoanType,
        )
      : undefined;

    const lender = location && loanTypeLabel
      ? matchOption(
          getLendersForLocationLoanType(config.bankLoanTypeLocations, location, loanTypeLabel),
          wantLender,
        )
      : undefined;

    if (location)      setSelectedLocation(location);
    if (loanTypeLabel) setSelectedLoanTypeLabel(loanTypeLabel);
    if (lender)        setSelectedLender(lender);

    setSearchParams({}, { replace: true });
  }, [configLoading, config.bankLoanTypeLocations, locationCards, searchParams, setSearchParams]);

  const availableLoanTypeLabels = selectedLocation
    ? getLoanTypesForLocation(config.bankLoanTypeLocations, selectedLocation)
    : [];
  const filteredLoanTypeLabels = useMemo(() =>
    availableLoanTypeLabels.filter(l => l.toLowerCase().includes(loanSearch.toLowerCase())),
    [availableLoanTypeLabels, loanSearch]);

  const availableLenders = selectedLocation && selectedLoanTypeLabel
    ? getLendersForLocationLoanType(config.bankLoanTypeLocations, selectedLocation, selectedLoanTypeLabel)
    : [];
  const filteredLenders = useMemo(() =>
    availableLenders.filter(l => l.toLowerCase().includes(lenderSearch.toLowerCase())),
    [availableLenders, lenderSearch]);

  const selectedLoanType = LABEL_TO_LOAN_TYPE[selectedLoanTypeLabel.trim().toLowerCase()];

  const selectedPayout = payouts.find(p => {
    if (p.lender !== selectedLender) return false;
    // Payouts created before the location field existed have no location set —
    // treat those as applying to any location so old data keeps showing up.
    if (p.location && p.location !== selectedLocation) return false;
    if (!selectedLoanType) return false;
    return p.loanType === selectedLoanType;
  });

  // step: 1 = pick location, 2 = pick loan type, 3 = pick lender, 4 = view result
  const step = !selectedLocation ? 1 : !selectedLoanType ? 2 : !selectedLender ? 3 : 4;

  // ── handlers ────────────────────────────────────────────────────────────────
  const handleCopyDSA = () => {
    if (selectedPayout) {
      navigator.clipboard.writeText(selectedPayout.dsaCode);
      toast.success("DSA Code copied to clipboard");
    }
  };

  const handleOpenAdd = () => {
    setEditPayout(null);
    setFormData({
      location: selectedLocation || "",
      lender: selectedLender || "",
      loanType: selectedLoanType ?? "home_loan",
      maxPayout: "",
      dsaCode: "",
      remarks: "",
    });
    setAddDialogOpen(true);
  };

  const handleOpenEdit = () => {
    if (selectedPayout) {
      setEditPayout(selectedPayout);
      setFormData({
        location: selectedPayout.location || selectedLocation || "",
        lender: selectedPayout.lender,
        loanType: selectedPayout.loanType,
        maxPayout: selectedPayout.maxPayout,
        dsaCode: selectedPayout.dsaCode,
        remarks: selectedPayout.remarks,
      });
      setAddDialogOpen(true);
    }
  };

  const handleSave = async () => {
    const missing = [
      !formData.location && "Location",
      !formData.lender && "Lender",
      !formData.maxPayout && "Max Payout",
      !formData.dsaCode && "DSA Code",
    ].filter(Boolean);
    if (missing.length > 0) {
      toast.error(`Please fill required fields: ${missing.join(", ")}`);
      return;
    }
    if (!user?.id) { toast.error("User not authenticated"); return; }

    try {
      const payoutData = {
        location: formData.location,
        lender: formData.lender,
        loanType: formData.loanType,
        maxPayout: formData.maxPayout,
        dsaCode: formData.dsaCode,
        slabs: [],
        remarks: formData.remarks,
      };
      if (editPayout) {
        await savePayoutRate(payoutData, user.id, editPayout.id);
        toast.success("Payout updated successfully");
      } else {
        await savePayoutRate(payoutData, user.id);
        toast.success("Payout added successfully");
      }
      setAddDialogOpen(false);
      loadPayouts();
    } catch (error) {
      console.error('Failed to save payout:', error);
      toast.error("Failed to save payout");
    }
  };

  const handleDelete = async () => {
    if (!selectedPayout) return;
    try {
      await deletePayoutRate(selectedPayout.id);
      setSelectedLender("");
      toast.success("Payout deleted successfully");
      loadPayouts();
    } catch (error) {
      console.error('Failed to delete payout:', error);
      toast.error("Failed to delete payout");
    }
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto pb-24 md:pb-8">

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/tools")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <PageHeader title="Code & Payouts" subtitle="View and manage payout details" />
          {canManage && (
            <Button
              size="sm"
              onClick={handleOpenAdd}
              disabled={loading}
              className="ml-auto shrink-0 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md hover:shadow-lg transition-all"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          )}
        </div>

        {(loading || configLoading) ? (
          <Card>
            <CardContent className="p-8 flex items-center justify-center">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Loading payout data...</span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">

            {/* ── Breadcrumb chip (shown once a location is chosen) ── */}
            {selectedLocation && (
              <Breadcrumb
                location={selectedLocation}
                loanType={selectedLoanTypeLabel || undefined}
                lender={selectedLender || undefined}
                onLocationClick={() => { setSelectedLocation(""); setSelectedLoanTypeLabel(""); setSelectedLender(""); setLoanSearch(""); setLenderSearch(""); }}
                onLoanTypeClick={() => { setSelectedLoanTypeLabel(""); setSelectedLender(""); setLenderSearch(""); }}
                onLenderClick={() => setSelectedLender("")}
              />
            )}

            {/* ── STEP 1: Select Location ── */}
            {step === 1 && (
              <div>
                <StepLabel n={1} label="Select location" />
                <SearchInput value={locationSearch} onChange={setLocationSearch} placeholder="Search for Location" />
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3">
                  {filteredLocationCards.map(loc => (
                    <PickCard key={loc.key} emoji={loc.emoji} label={loc.label} selected={selectedLocation === loc.key}
                      onClick={() => { setSelectedLocation(loc.key); setSelectedLoanTypeLabel(""); setSelectedLender(""); setLoanSearch(""); setLenderSearch(""); }} />
                  ))}
                  {filteredLocationCards.length === 0 && (
                    <p className="col-span-full text-center text-sm text-muted-foreground py-6">
                      {locationCards.length === 0
                        ? "No locations configured yet. Add lender/loan-type/location mappings in Admin."
                        : "No locations found"}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 2: Select Loan Type (location chosen, no loan type yet) ── */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                <StepLabel n={2} label="Select loan type" />
                <SearchInput value={loanSearch} onChange={setLoanSearch} placeholder="Search for Loan Type" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                  {filteredLoanTypeLabels.map(label => (
                    <PickCard key={label} emoji={LOAN_TYPE_ICONS[label] || "📋"} label={label} selected={selectedLoanTypeLabel === label}
                      onClick={() => { setSelectedLoanTypeLabel(label); setSelectedLender(""); setLenderSearch(""); }} />
                  ))}
                  {filteredLoanTypeLabels.length === 0 && (
                    <p className="col-span-full text-center text-sm text-muted-foreground py-6">No loan types found</p>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── STEP 3: Select Lender (location + loan type chosen) ── */}
            {step === 3 && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                <StepLabel n={3} label="Select lender" />
                <SearchInput value={lenderSearch} onChange={setLenderSearch} placeholder="Search for Lender" />
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
                  {filteredLenders.map(lender => (
                    <LenderCard key={lender} lender={lender} loanType={selectedLoanTypeLabel}
                      selected={selectedLender === lender} onClick={() => setSelectedLender(lender)}
                      bankLogoUrls={config.bankLogoUrls} />
                  ))}
                  {filteredLenders.length === 0 && (
                    <p className="col-span-full text-center text-sm text-muted-foreground py-6">No lenders found</p>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── STEP 4: Payout Details ── */}
            {step === 4 && selectedPayout && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Max Payout & DSA Code */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-accent/10 border-accent/20">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 text-accent mb-2">
                        <Percent className="h-5 w-5" />
                        <span className="text-sm font-medium">Max Payout</span>
                      </div>
                      <p className="text-3xl font-bold text-accent">{selectedPayout.maxPayout}</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-primary/10 border-primary/20">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 text-primary mb-2">
                        <Building2 className="h-5 w-5" />
                        <span className="text-sm font-medium">DSA Code</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-bold text-primary truncate">{selectedPayout.dsaCode}</p>
                        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={handleCopyDSA}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Remarks */}
                {selectedPayout.remarks && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium mb-1">Remarks & Slabs</p>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedPayout.remarks}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={handleOpenEdit}>
                    <Edit2 className="h-4 w-4 mr-2" />Edit
                  </Button>
                  <Button variant="destructive" className="flex-1" onClick={handleDelete}>
                    <Trash2 className="h-4 w-4 mr-2" />Delete
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── Empty state when step 4 but no payout data found ── */}
            {step === 4 && !selectedPayout && (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No payout data found for this selection</p>
                </CardContent>
              </Card>
            )}

          </div>
        )}

        {/* Add/Edit Sheet */}
        <Sheet open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl flex flex-col">
            <SheetHeader className="pb-4 px-4 flex-shrink-0">
              <SheetTitle className="text-xl font-bold text-center">
                {editPayout ? 'Edit Payout' : 'Add New Payout'}
              </SheetTitle>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6">
              <div className="space-y-5 pb-8">

                {/* SECTION: Location & Lender */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <MapPin className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">Location & Lender</h3>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Location *</Label>
                    <Select value={formData.location} onValueChange={(v) => setFormData(p => ({ ...p, location: v }))}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select location" /></SelectTrigger>
                      <SelectContent>
                        {locationCards.map(loc => (
                          <SelectItem key={loc.key} value={loc.key}>{loc.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Lender *</Label>
                    <Input
                      value={formData.lender}
                      onChange={(e) => setFormData(p => ({ ...p, lender: e.target.value }))}
                      placeholder="e.g., HDFC Bank"
                      className="h-12"
                    />
                  </div>
                </div>

                {/* SECTION: Loan & Payout Details */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <Percent className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">Loan & Payout Details</h3>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Loan Type *</Label>
                    <Select value={formData.loanType} onValueChange={(v) => setFormData(p => ({ ...p, loanType: v as LoanType }))}>
                      <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {loanTypes.map(type => (
                          <SelectItem key={type} value={type}>{LOAN_TYPE_LABELS[type]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Max Payout *</Label>
                    <Input
                      value={formData.maxPayout}
                      onChange={(e) => setFormData(p => ({ ...p, maxPayout: e.target.value }))}
                      placeholder="e.g., 1.5%"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">DSA Code *</Label>
                    <Input
                      value={formData.dsaCode}
                      onChange={(e) => setFormData(p => ({ ...p, dsaCode: e.target.value }))}
                      placeholder="e.g., HDFC-DSA-2024"
                      className="h-12"
                    />
                  </div>
                </div>

                {/* SECTION: Remarks */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <FileText className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">Remarks</h3>
                  </div>
                  <div className="space-y-2">
                    <Textarea
                      value={formData.remarks}
                      onChange={(e) => setFormData(p => ({ ...p, remarks: e.target.value }))}
                      placeholder="e.g., Slabs: <1Cr: 0.5%, 1-2Cr: 0.6%, etc."
                      rows={5}
                      className="resize-none"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSave}
                  className="w-full h-12 mt-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold"
                >
                  {editPayout ? 'Update Payout' : 'Add Payout'}
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

      </div>
    </AppLayout>
  );
}
